import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Store } from "lucide-react";
import { useUpdateResource } from "../../../hooks/useUpdateResource";
import useOpeningHoursForm, {
    OpeningHoursApiDay,
    OpeningHoursFormData,
} from "../../../hooks/useOpeningHoursForm";

import LoadingErrorState from "../../../components/ui/LoadingErrorState";
import OpeningHoursInputs from "../../../components/form/OpeningHoursInputs";
import LoadingButton from "../../../components/form/LoadingButton";
import SubmitButton from "../../../components/form/SubmitButton";
import SubTitle from "../../../components/ui/SubTitle";

import { useProductOpeningHours } from "../../../hooks/store/productOpeningHours";

const ProductOpeningHours: React.FC = () => {
    const { t } = useTranslation();

    const {
        storeId,
        productId,
    } = useParams<{
        storeId?: string;
        productId: string;
    }>();

    const numericStoreId = storeId
        ? Number(storeId)
        : undefined;

    const numericProductId = Number(productId);

    const {
        data: openingHours,
        isError,
        isLoading,
    } = useProductOpeningHours(
        numericStoreId,
        numericProductId
    );

    const {
        control,
        fields,
        watch,
        handleSubmit,
        activeDays,
        toggleDay,
        addPeriod,
        removePeriod,
        applied,
        handleApplyToggle,
        setOpeningHours,
        buildPayload,
    } = useOpeningHoursForm();

    const {
        loading,
        updateResource,
    } = useUpdateResource();

    useEffect(() => {
        if (!openingHours?.days) {
            return;
        }

        const apiDays: OpeningHoursApiDay[] =
            openingHours.days.map((day) => ({
                day: day.day,
                periods: day.periods.map((period) => ({
                    open: period.open,
                    close: period.close,
                })),
            }));

        setOpeningHours(apiDays);
    }, [
        openingHours,
        setOpeningHours,
    ]);

    const onSubmit = (
        data: OpeningHoursFormData
    ) => {
        if (!openingHours || !numericProductId) {
            return;
        }

        const payload = buildPayload(data);

        const url = numericStoreId
            ? `product-opening-hours/current/${numericProductId}?storeId=${numericStoreId}`
            : `product-opening-hours/current/${numericProductId}`;

        updateResource(
            url,
            payload,
            {
                method: "PUT",
                successMessage: t("update_success"),
                invalidateQueries: [
                    "product-opening-hours",
                ],
            }
        );
    };

    const isInherited =
        openingHours?.inheritSchedule === true;

    return (
        <div>
            <LoadingErrorState
                isError={isError}
                isLoading={isLoading}
            />

            {openingHours && (
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-6 w-full"
                >
                    <SubTitle
                        title={t("opening_hours")}
                    />

                    {isInherited && (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
                            <div className="flex items-start gap-4">
                                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-200 shrink-0">
                                    <Store
                                        size={20}
                                        className="text-gray-500"
                                    />
                                </div>

                                <div>
                                    <p className="font-medium text-gray-900">
                                        {t(
                                            "store_opening_hours"
                                        )}
                                    </p>

                                    <p className="text-sm text-gray-500 mt-1">
                                        {t(
                                            "product_uses_store_opening_hours_description"
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <OpeningHoursInputs
                        control={control}
                        fields={fields}
                        watch={watch}
                        activeDays={activeDays}
                        toggleDay={toggleDay}
                        addPeriod={addPeriod}
                        removePeriod={removePeriod}
                        applied={applied}
                        handleApplyToggle={
                            handleApplyToggle
                        }
                    />

                    <div>
                        {loading ? (
                            <LoadingButton />
                        ) : (
                            <SubmitButton
                                label="update"
                            />
                        )}
                    </div>
                </form>
            )}
        </div>
    );
};

export default ProductOpeningHours;